import useGetInternalWidgetConfig from '@hooks/api/queries/widgets/useGetInternalWidgetConfig';
import useSubscription from '@src/hooks/useSubscription';
import HorizontalLine from '@src/sld/components/Line/HorizontalLine';
import VerticalLine from '@src/sld/components/Line/VerticalLine';
import MultiSwitchConnector from '@src/sld/components/MultiSwitchConnector/MultiSwitchConnector';
import Resistor from '@src/sld/components/Resistor/Resistor';
import { getDataPointCollectionByPointNames } from '@src/sld/utils';
import { DataPointWithValue } from '@src/types/DataPoint';
import { SiteMeta } from '@src/types/SiteMeta';
import { WidgetTypeDeviceList } from '@src/types/Widget';
import storeConnector from '@store/storeConnector';
import { ReactElement } from 'react';
import BreakerBasic from '../../components/Breaker/BreakerBasic';
import BreakerStack from './components/BreakerStack';
import Header from './components/Header';
import { Hanford_1_Device_Points_Config } from './devicePointsConfig';

type Hanford_1_SLDProps = {
  siteMeta: SiteMeta;
};

function Hanford_1_Diagram(props: Hanford_1_SLDProps): ReactElement {
  const { siteMeta } = props;

  type EngineeringShapeConfig = {
    shape: string;
    internalId: string;
    dataSourceDeviceType: string;
    label: string;
    dataSourceDeviceEngineeringId: string;
    points: {
      label: string;
      pointName: string;
      isStatusBinaryPoint: boolean;
    }[];
    dataPoints: DataPointWithValue[] | [];
  };

  const M1_Breaker_Config: EngineeringShapeConfig | undefined =
    Hanford_1_Device_Points_Config.M1_BREAKER_STATUS;
  const M1_Breaker_Points_Config: EngineeringShapeConfig | undefined =
    Hanford_1_Device_Points_Config.M1_BREAKER_POINTS;
  const F1_1_Breaker_Config: EngineeringShapeConfig | undefined =
    Hanford_1_Device_Points_Config.F1_1_BREAKER_STATUS;
  const F1_1_Breaker_Points_Config: EngineeringShapeConfig | undefined =
    Hanford_1_Device_Points_Config.F1_1_BREAKER_POINTS;
  const F1_2_Breaker_Config: EngineeringShapeConfig | undefined =
    Hanford_1_Device_Points_Config.F1_2_BREAKER_STATUS;
  const F1_2_Breaker_Points_Config: EngineeringShapeConfig | undefined =
    Hanford_1_Device_Points_Config.F1_2_BREAKER_POINTS;
  const A1_Breaker_Config: EngineeringShapeConfig | undefined =
    Hanford_1_Device_Points_Config.A1_BREAKER_STATUS;
  const A1_Breaker_Points_Config: EngineeringShapeConfig | undefined =
    Hanford_1_Device_Points_Config.A1_BREAKER_POINTS;

  const { data: rtacWidgetConfig } =
    useGetInternalWidgetConfig<WidgetTypeDeviceList>(
      'hanford_1_sld_rtac',
      {
        sourceDeviceId: siteMeta.siteId,
      },
      { enabled: true },
    );

  const sldDataPointsWithValues = useSubscription(
    rtacWidgetConfig?.dataPoints || [],
  );

  const getDataPointsForThisDevice = (
    dataSourceDeviceEngineeringId: string,
  ): DataPointWithValue[] => {
    return sldDataPointsWithValues.filter(
      (point) => point.sourceDeviceId === dataSourceDeviceEngineeringId,
    );
  };

  if (sldDataPointsWithValues.length > 0) {
    M1_Breaker_Config.dataPoints = getDataPointCollectionByPointNames(
      getDataPointsForThisDevice(
        M1_Breaker_Config.dataSourceDeviceEngineeringId,
      ),
      M1_Breaker_Config.points.map((point) => point.pointName.toLowerCase()) ||
        [],
    );

    M1_Breaker_Points_Config.dataPoints = getDataPointCollectionByPointNames(
      getDataPointsForThisDevice(
        M1_Breaker_Points_Config.dataSourceDeviceEngineeringId,
      ),
      M1_Breaker_Points_Config.points.map((point) =>
        point.pointName.toLowerCase(),
      ) || [],
    );

    F1_1_Breaker_Config.dataPoints = getDataPointCollectionByPointNames(
      getDataPointsForThisDevice(
        F1_1_Breaker_Config.dataSourceDeviceEngineeringId,
      ),
      F1_1_Breaker_Config.points.map((point) =>
        point.pointName.toLowerCase(),
      ) || [],
    );

    F1_1_Breaker_Points_Config.dataPoints = getDataPointCollectionByPointNames(
      getDataPointsForThisDevice(
        F1_1_Breaker_Points_Config.dataSourceDeviceEngineeringId,
      ),
      F1_1_Breaker_Points_Config.points.map((point) =>
        point.pointName.toLowerCase(),
      ) || [],
    );

    F1_2_Breaker_Config.dataPoints = getDataPointCollectionByPointNames(
      getDataPointsForThisDevice(
        F1_2_Breaker_Config.dataSourceDeviceEngineeringId,
      ),
      F1_2_Breaker_Config.points.map((point) =>
        point.pointName.toLowerCase(),
      ) || [],
    );

    F1_2_Breaker_Points_Config.dataPoints = getDataPointCollectionByPointNames(
      getDataPointsForThisDevice(
        F1_2_Breaker_Points_Config.dataSourceDeviceEngineeringId,
      ),
      F1_2_Breaker_Points_Config.points.map((point) =>
        point.pointName.toLowerCase(),
      ) || [],
    );

    A1_Breaker_Config.dataPoints = getDataPointCollectionByPointNames(
      getDataPointsForThisDevice(
        A1_Breaker_Config.dataSourceDeviceEngineeringId,
      ),
      A1_Breaker_Config.points.map((point) => point.pointName.toLowerCase()) ||
        [],
    );

    A1_Breaker_Points_Config.dataPoints = getDataPointCollectionByPointNames(
      getDataPointsForThisDevice(
        A1_Breaker_Points_Config.dataSourceDeviceEngineeringId,
      ),
      A1_Breaker_Points_Config.points.map((point) =>
        point.pointName.toLowerCase(),
      ) || [],
    );
  }

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      xlinkHref='http://www.w3.org/1999/xlink'
      viewBox='200 40 1200 700'
      xmlSpace='preserve'
      colorInterpolationFilters='sRGB'
      className='st10'
      style={{ fill: 'none', stroke: 'currentColor', overflowX: 'auto' }}
    >
      <title>Hanford 1 SLD</title>
      <Header />

      {/* top breaker */}
      <g transform='translate(728 146)'>
        <BreakerBasic
          labels={[{ label: 'CB 152-6', xCoordinate: 40, yCoordinate: 15 }]}
          translateXCoordinate={0}
          translateYCoordinate={0}
          dataPointsWithValues={[]}
          defaultHot={true}
        />
      </g>

      {/* vertical line below top breaker */}
      <g transform='translate(742 163)'>
        <VerticalLine
          translateXCoordinate={0}
          translateYCoordinate={11}
          length={40}
          color='white'
          width={0.5}
        />
      </g>

      {/* resistor */}
      <g transform='rotate(90 700 783) translate(127 -35)'>
        <Resistor label={''} />
      </g>

      {/* resistor text */}
      <g transform='translate(770 230)'>
        <text
          x={0}
          y={0}
          fontSize={8}
          fill='white'
          stroke='white'
          strokeWidth={0.2}
        >
          HANBESSMPT
        </text>
      </g>

      {/* vertical line above connector */}
      <g transform='translate(778 255)'>
        <VerticalLine
          translateXCoordinate={-35}
          translateYCoordinate={-17}
          length={62}
          color='white'
          width={0.5}
        />
      </g>

      {/* connector above breaker stacks */}
      <g transform='translate(500 -200)'>
        <MultiSwitchConnector
          translateXCoordinate={80}
          translateYCoordinate={-45}
          horizontalLength={300}
          verticalLength={20}
          startingXCoordinate={0}
        />
      </g>

      {/* breaker stack 1 */}
      <g transform='translate(693 -403)'>
        <BreakerStack
          breakerConnected={
            A1_Breaker_Config.dataPoints.find(
              (point) =>
                point.pointName ===
                A1_Breaker_Config.points.find(
                  (point) => point.isStatusBinaryPoint,
                )?.pointName,
            )?.value == 1 || false
          }
          deviceDataPoints={A1_Breaker_Points_Config.dataPoints.filter(
            (point) =>
              point.pointName !==
              A1_Breaker_Config.points.find((p) => p.isStatusBinaryPoint)
                ?.pointName,
          )}
          translateXCoordinate={0}
          translateYCoordinate={0}
          feederALabel={''}
          breakerLabel={'52-A1'}
          sectionLabel={'52-A1'}
          deviceDataPointsSectionXCoordinate={-340}
          deviceDataPointsSectionYCoordinate={750}
          verticalLineLength={50}
          displayArrowDown={true}
          displayArrowDownYCoordinate={795}
        />

        <text x={-140} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          AUX FEED #1
        </text>
      </g>

      {/* breaker stack 2 */}
      <g transform='translate(993 -403)'>
        <BreakerStack
          breakerConnected={
            M1_Breaker_Config.dataPoints.find(
              (point) =>
                point.pointName ===
                M1_Breaker_Config.points.find(
                  (point) => point.isStatusBinaryPoint,
                )?.pointName,
            )?.value == 1 || false
          }
          deviceDataPoints={M1_Breaker_Points_Config.dataPoints.filter(
            (point) =>
              point.pointName !==
              M1_Breaker_Config.points.find((p) => p.isStatusBinaryPoint)
                ?.pointName,
          )}
          translateXCoordinate={0}
          translateYCoordinate={0}
          feederALabel={''}
          breakerLabel={'52-M1'}
          sectionLabel={'52-M1'}
          deviceDataPointsSectionXCoordinate={-30}
          deviceDataPointsSectionYCoordinate={750}
          displayArrowDown={false}
          verticalLineLength={50}
        />
      </g>

      <g transform='translate(779 391)'>
        <HorizontalLine
          translateXCoordinate={0}
          translateYCoordinate={0}
          length={100}
          color='white'
          width={0.5}
        />

        <VerticalLine
          translateXCoordinate={0}
          translateYCoordinate={0}
          length={50}
          color='white'
          width={0.5}
        />

        <VerticalLine
          translateXCoordinate={100}
          translateYCoordinate={0}
          length={50}
          color='white'
          width={0.5}
        />
      </g>

      {/* breaker stack 3 */}
      <g transform='translate(893 -282)'>
        <BreakerStack
          breakerConnected={
            F1_1_Breaker_Config.dataPoints.find(
              (point) =>
                point.pointName ===
                F1_1_Breaker_Config.points.find(
                  (point) => point.isStatusBinaryPoint,
                )?.pointName,
            )?.value == 1 || false
          }
          deviceDataPoints={F1_1_Breaker_Points_Config.dataPoints.filter(
            (point) =>
              point.pointName !==
              F1_1_Breaker_Config.points.find((p) => p.isStatusBinaryPoint)
                ?.pointName,
          )}
          translateXCoordinate={0}
          translateYCoordinate={0}
          feederALabel={''}
          breakerLabel={'52F1-1'}
          sectionLabel={'52F1-1'}
          deviceDataPointsSectionXCoordinate={-350}
          deviceDataPointsSectionYCoordinate={800}
          displayArrowDown={true}
          displayArrowDownYCoordinate={795}
          verticalLineLength={50}
        />

        <text x={-140} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          BESS CIRCUIT
        </text>

        <text x={-125} y={830} fill='currentColor' stroke='none' fontSize='8px'>
          NO. 1-1
        </text>

        <text x={-147} y={840} fill='currentColor' stroke='none' fontSize='8px'>
          18 PCS MODULES
        </text>
      </g>

      {/* breaker stack 4 */}
      <g transform='translate(990 -282)'>
        <BreakerStack
          breakerConnected={
            F1_2_Breaker_Config.dataPoints.find(
              (point) =>
                point.pointName ===
                F1_2_Breaker_Config.points.find(
                  (point) => point.isStatusBinaryPoint,
                )?.pointName,
            )?.value == 1 || false
          }
          deviceDataPoints={F1_2_Breaker_Points_Config.dataPoints.filter(
            (point) =>
              point.pointName !==
              F1_2_Breaker_Config.points.find((p) => p.isStatusBinaryPoint)
                ?.pointName,
          )}
          translateXCoordinate={0}
          translateYCoordinate={0}
          feederALabel={''}
          breakerLabel={'52F1-2'}
          sectionLabel={'52F1-2'}
          deviceDataPointsSectionXCoordinate={-25}
          deviceDataPointsSectionYCoordinate={795}
          displayArrowDown={true}
          displayArrowDownYCoordinate={795}
          verticalLineLength={50}
        />

        <text x={-140} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          BESS CIRCUIT
        </text>

        <text x={-125} y={830} fill='currentColor' stroke='none' fontSize='8px'>
          NO. 1-2
        </text>

        <text x={-147} y={840} fill='currentColor' stroke='none' fontSize='8px'>
          17 PCS MODULES
        </text>
      </g>
    </svg>
  );
}

export default storeConnector(Hanford_1_Diagram, {
  config: ['siteMeta'],
});
